import {
	ChangeDetectionStrategy,
	Component,
	computed,
	inject,
	input,
} from '@angular/core';

import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import dayjs from 'dayjs';

import { ReceiptSummaryComponent } from '@valk-nx/compositions/ui-receipt-summary/src/lib/receipt-summary.component';
import type {
	Item,
	ItemWithExtras,
	SpecifiedTaxes,
} from '@valk-nx/compositions/ui-receipt-summary/src/lib/receipt-summary.interface';
import { SVG_ICON_IMPORTS } from '@valk-nx/helpers/lib/svg-unique-ids/svg-unique-ids.directive';

import type { PricingSummaryOccupancy } from './pricing-summary.interface';

import 'dayjs/locale/de';
import 'dayjs/locale/en';
import 'dayjs/locale/es';
import 'dayjs/locale/fr';
import 'dayjs/locale/nl';

@Component({
	changeDetection: ChangeDetectionStrategy.OnPush,
	selector: `vp-pricing-summary`,
	templateUrl: './pricing-summary.html',
	imports: [SVG_ICON_IMPORTS, ReceiptSummaryComponent, TranslatePipe],
})
export class PricingSummaryComponent {
	private readonly translate = inject(TranslateService);

	currency = input<string>('EUR');
	items = input.required<(Item | ItemWithExtras)[]>();
	taxes = input.required<SpecifiedTaxes | number>();
	total = input.required<number>();
	occupancy = input<PricingSummaryOccupancy | undefined>(undefined);
	dateRange = input<{ from: string; to: string } | undefined>(undefined);

	startDate = computed(() => {
		if (!this.dateRange()?.from) return null;
		return dayjs(this.dateRange().from).format('dd DD MMM YYYY');
	});
	endDate = computed(() => {
		if (!this.dateRange()?.to) return null;
		return dayjs(this.dateRange().to).format('dd DD MMM YYYY');
	});

	get occupancyString(): string {
		const occupancy = this.occupancy();
		if (!occupancy) return null;

		const { rooms, persons } = occupancy;

		const roomsKey =
			rooms === 1
				? 'global.occupancy-combination-rooms-singular-text'
				: 'global.occupancy-combination-rooms-plural-text';

		const personsKey =
			persons === 1
				? 'global.occupancy-combination-adults-singular-text'
				: 'global.occupancy-combination-adults-plural-text';

		const parts: string[] = [];

		if (rooms) {
			parts.push(this.translate.instant(roomsKey, { amount: rooms }));
		}
		if (persons) {
			parts.push(this.translate.instant(personsKey, { amount: persons }));
		}

		return parts.join(', ');
	}
}
